/**
 * Drop shadow letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dropShadowLettersPack: ToilePack = {
    slug: 'drop-shadow-letters',
    category: 'illustration',
    name: 'Drop shadow letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'solid', label: 'Solid shadow', subject: 'the word "BOLD" in heavy capitals with a solid dark drop shadow offset down and to the right'},
        {key: 'long', label: 'Long shadow', subject: 'the word "SALE" in block capitals with a long diagonal shadow running to the corner of the frame'},
        {key: 'script', label: 'Script shadow', subject: 'the word "Hello" in a looped script with a thin offset shadow and a few small highlight dashes'},
    ],
    styles: [
        {key: 'poster', label: 'Poster', style: 'flat vector poster lettering with hard edges and smooth gradient-free fills', palette: 'A limited palette of four colors, bright letters with a dark shadow, on a solid mustard background.', framing: 'The word is centered with an even margin.'},
        {key: 'marker', label: 'Marker', style: 'hand-drawn marker lettering with a bold black outline and a hatched shadow', palette: 'Red letters, black shadow and a blue accent on a white paper background.', framing: 'The word is centered with a wide margin of empty paper.'},
        {key: 'screenprint', label: 'Screen print', style: 'a two-pass screen print with slight misregistration and subtle halftone texture', palette: 'Two inks, teal and orange, on a cream background.', framing: 'The word is centered with a generous margin.'},
    ],
    directions: ['pink shadow', 'two words', 'your name'],
    directionHint: 'try: pink shadow',
    seo: {
        title: 'Drop Shadow Letters: Solid, Long and Script Shadows',
        metaDescription: 'Nine drop shadow lettering pieces: solid, long and script shadows on BOLD, SALE and Hello, for posters and titles.',
        h1: 'Drop shadow letters: a solid shadow, a long shadow, a script shadow',
        tagline: 'One shadow device on three words, shown in three treatments.',
        intro: 'Drop shadow lettering is the simplest way to make lettering stand off the page: a shadow cast to one side. The nine pieces show BOLD with a solid shadow, SALE with a long diagonal shadow, and Hello in looped script with a thin shadow. Each word is set as flat poster lettering, hand-drawn marker, or a two-pass screen print. Ask for pink shadow, two words or your name.',
        useCases: [
            'a poster headline',
            'a title on a notebook page',
            'a sale sign',
            'a greeting card',
        ],
        faq: [
            {q: 'Which direction does the shadow fall?', a: 'The same direction on every piece, down and to one side, so a set of letters reads consistently.'},
            {q: 'What is the difference between the three treatments?', a: 'Poster is flat vector with hard edges. Marker has a bold black outline and a hatched shadow. Screen print is a two-pass print with slight misregistration.'},
            {q: 'Are the words fixed?', a: 'The samples are BOLD, SALE and Hello. Other words are a request.'},
            {q: 'What shadow does each word have?', a: 'BOLD has a solid shadow. SALE has a long diagonal shadow. Hello has a thin offset shadow with a few small highlights.'},
        ],
    },
}
